/**
 * PitfallDictionary — queue #666 §UAT-2026-07-09-R666 §R666-4.
 *
 * 전역 '함정 사전' 뷰 기본 화면 — 증상 키워드 텍스트 필터 + 심각도 세그먼트 + 모듈
 * 파셋 필터(§R640 ModuleFacetBar 계보) + 카드 그리드(`PitfallCard.tsx`). 필터 상태는
 * URL 쿼리(`?q=&sev=&code=`)가 단일 진실이다(§R640 URL-as-SSOT 계보 · 카탈로그 검색과
 * 동일 `q` 파라미터를 재사용한다 — 뷰 전환 시 동일 문자열 필터 의미를 유지).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@ui-kit';
import type { Pitfall, PitfallSeverity } from '../data/pitfalls';
import { filterPitfalls } from '../data/pitfalls';
import { PitfallCard } from './PitfallCard';

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-1)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  fontFamily: 'var(--font-family-sans)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
};

const SEVERITY_OPTIONS: PitfallSeverity[] = ['high', 'medium', 'low'];

function isPitfallSeverity(v: string): v is PitfallSeverity {
  return SEVERITY_OPTIONS.includes(v as PitfallSeverity);
}

interface Props {
  pitfalls: Pitfall[];
  onSelect: (id: string) => void;
}

export function PitfallDictionary({ pitfalls, onSelect }: Props) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get('q') ?? '';
  const severityParam = searchParams.get('sev') ?? '';
  const severity = isPitfallSeverity(severityParam) ? severityParam : undefined;
  const moduleCode = searchParams.get('code') ?? undefined;

  const moduleOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of pitfalls) for (const code of p.moduleCodes) set.add(code);
    return [...set].sort();
  }, [pitfalls]);

  function setParam(key: 'q' | 'sev' | 'code', value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  function resetFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    next.delete('sev');
    next.delete('code');
    setSearchParams(next, { replace: true });
  }

  const filtered = useMemo(
    () => filterPitfalls(pitfalls, { query, severity, moduleCode }, t),
    [pitfalls, query, severity, moduleCode, t]
  );

  const hasActiveFilters = Boolean(query || severity || moduleCode);

  return (
    <section aria-label={t('showcase.pitfall.dictionary.ariaLabel')}>
      <h2
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-1) 0',
        }}
      >
        {t('showcase.pitfall.dictionary.title')}
      </h2>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          margin: '0 0 var(--spacing-4) 0',
        }}
      >
        {t('showcase.pitfall.dictionary.subtitle')}
      </p>

      <div
        role="search"
        aria-label={t('showcase.pitfall.filter.barAria')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          alignItems: 'flex-end',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <label style={{ ...labelStyle, flex: '1 1 240px' }}>
          <span>{t('showcase.pitfall.filter.query')}</span>
          <input
            type="search"
            style={inputStyle}
            value={query}
            onChange={(e) => setParam('q', e.target.value)}
            aria-label={t('showcase.pitfall.filter.query')}
          />
        </label>

        <label style={labelStyle}>
          <span>{t('showcase.pitfall.filter.severity.label')}</span>
          <select
            style={selectStyle}
            value={severityParam}
            onChange={(e) => setParam('sev', e.target.value)}
            aria-label={t('showcase.pitfall.filter.severity.label')}
          >
            <option value="">{t('showcase.pitfall.filter.severity.all')}</option>
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`showcase.pitfall.severity.${s}`)}
              </option>
            ))}
          </select>
        </label>

        <label style={labelStyle}>
          <span>{t('showcase.pitfall.filter.module.label')}</span>
          <select
            style={selectStyle}
            value={moduleCode ?? ''}
            onChange={(e) => setParam('code', e.target.value)}
            aria-label={t('showcase.pitfall.filter.module.label')}
          >
            <option value="">{t('showcase.pitfall.filter.module.all')}</option>
            {moduleOptions.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={t('showcase.pitfall.empty.title')}
          action={
            hasActiveFilters ? (
              <button
                type="button"
                onClick={resetFilters}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('showcase.pitfall.empty.reset')}
              </button>
            ) : undefined
          }
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 'var(--spacing-4)',
          }}
        >
          {filtered.map((pitfall) => (
            <PitfallCard key={pitfall.id} pitfall={pitfall} onSelect={onSelect} />
          ))}
        </div>
      )}
    </section>
  );
}
